import React, { useState, useEffect, useRef, useCallback } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileBarChart, RefreshCw, Scan, Loader2, Lock, ShieldAlert, Clock, CheckCircle2, Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import UsnStickerDialog from '@/components/print/UsnStickerDialog';
import { getMe } from '@/services/authApi';

const ADMIN_ROLES = ['Admin', 'super_admin', 'Site Admin'];
const OPEN_STATUSES = ['AT_JOBBER', 'RECEIVING'];

const fmtAge = (h) => {
  if (h == null) return '—';
  if (h < 24) return `${h.toFixed(0)}h`;
  const d = Math.floor(h / 24);
  const rem = Math.round(h - d * 24);
  return rem ? `${d}d ${rem}h` : `${d}d`;
};

const statusClass = (s) => {
  if (s === 'COMPLETED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s === 'RECEIVING') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s === 'AT_JOBBER') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

const JwOrderReport = () => {
  const navigate = useNavigate();
  const [stickerJw, setStickerJw] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);

  // Pure Close dialog
  const [pureOrder, setPureOrder] = useState(null);
  const [pureTally, setPureTally] = useState(null);
  const [compUsn, setCompUsn] = useState('');
  const [pureBusy, setPureBusy] = useState(false);
  const compRef = useRef(null);

  // Force Close dialog
  const [forceOrder, setForceOrder] = useState(null);
  const [forceRemarks, setForceRemarks] = useState('');
  const [forceBusy, setForceBusy] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await apiClient.get('/flexi/jw-orders/report', { params });
      setRows(data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => {
    getMe().then((u) => setIsAdmin(ADMIN_ROLES.includes(u?.role))).catch(() => setIsAdmin(false));
  }, []);

  // ---- Pure Close ----
  const openPure = async (o) => {
    setPureOrder(o); setCompUsn(''); setPureTally(null);
    try {
      const { data } = await apiClient.get(`/flexi/jw-orders/${o.jw_id}/close-preview`);
      setPureTally(data.tally);
      setTimeout(() => compRef.current?.focus(), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load close preview');
    }
  };

  const scanComponent = async (e) => {
    e?.preventDefault?.();
    const u = compUsn.trim();
    if (!u || !pureOrder) return;
    setPureBusy(true);
    try {
      const { data } = await apiClient.post(`/flexi/jw-orders/${pureOrder.jw_id}/return-component-usn`, { usn: u });
      setPureTally({
        sent_qty: data.sent_qty, receipted_qty: data.receipted_qty,
        returned_component_qty: data.returned_component_qty, balance_qty: data.balance_qty,
      });
      toast.success(`Returned component ${u} (balance ${data.balance_qty})`);
      setCompUsn('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Component scan failed');
      setCompUsn('');
    } finally {
      setPureBusy(false);
      setTimeout(() => compRef.current?.focus(), 40);
    }
  };

  const confirmPureClose = async () => {
    if (!pureOrder) return;
    setPureBusy(true);
    try {
      await apiClient.post(`/flexi/jw-orders/${pureOrder.jw_id}/pure-close`);
      toast.success(`${pureOrder.jw_id} pure-closed`);
      setPureOrder(null);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pure close failed');
    } finally {
      setPureBusy(false);
    }
  };

  // ---- Force Close ----
  const confirmForceClose = async () => {
    if (!forceOrder) return;
    if (!forceRemarks.trim()) { toast.error('Remarks are mandatory'); return; }
    setForceBusy(true);
    try {
      await apiClient.post(`/flexi/jw-orders/${forceOrder.jw_id}/force-close`, { remarks: forceRemarks.trim() });
      toast.success(`${forceOrder.jw_id} force-closed`);
      setForceOrder(null); setForceRemarks('');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Force close failed');
    } finally {
      setForceBusy(false);
    }
  };

  return (
    <StaffScanGate stationName="Flexi — JW Order Report">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="jw-order-report">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/sections/flexi')}
                className="text-[#F9FAFB] hover:bg-white/10">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <FileBarChart className="w-7 h-7" />
                <div>
                  <h1 className="text-xl font-bold font-display">Job Order Report</h1>
                  <p className="text-sm text-[#E8DCC8]">Sent vs receipted, balance & close</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44 bg-white/10 border-white/20 text-white" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="AT_JOBBER">At Jobber</SelectItem>
                  <SelectItem value="RECEIVING">Receiving</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchReport} disabled={loading}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b bg-gray-50/60 flex items-center justify-between rounded-t-lg">
                <span className="text-sm font-medium text-gray-600" data-testid="report-count">
                  {loading ? 'Loading…' : `${rows.length} job order${rows.length === 1 ? '' : 's'}`}
                </span>
                <span className="text-xs text-gray-400">Order Qty = Replace USNs sent to jobber</span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="pl-4">JW ID</TableHead>
                      <TableHead>Assembly</TableHead>
                      <TableHead>Jobber</TableHead>
                      <TableHead className="text-right">Order Qty</TableHead>
                      <TableHead className="text-right">Receipted</TableHead>
                      <TableHead className="text-right">Returned (Comp)</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={10} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
                      </TableCell></TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400" data-testid="empty-row">
                        No job orders found
                      </TableCell></TableRow>
                    ) : rows.map((o) => {
                      const canClose = OPEN_STATUSES.includes(o.status);
                      return (
                        <TableRow key={o.jw_id} data-testid={`jwr-row-${o.jw_id}`}>
                          <TableCell className="font-mono text-xs font-semibold pl-4">{o.jw_id}</TableCell>
                          <TableCell className="text-sm">{o.assembly_item}</TableCell>
                          <TableCell className="text-xs text-gray-500">{o.jobber_name || '—'}</TableCell>
                          <TableCell className="text-right font-semibold" data-testid={`jwr-sent-${o.jw_id}`}>{o.sent_qty}</TableCell>
                          <TableCell className="text-right font-semibold text-emerald-700" data-testid={`jwr-recv-${o.jw_id}`}>{o.receipted_qty}</TableCell>
                          <TableCell className="text-right text-amber-700" data-testid={`jwr-ret-${o.jw_id}`}>{o.returned_component_qty}</TableCell>
                          <TableCell className={`text-right font-bold ${o.balance_qty === 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`jwr-bal-${o.jw_id}`}>{o.balance_qty}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline" className={statusClass(o.status)}>{o.status}</Badge>
                              {o.close_type && (
                                <Badge variant="outline" className={o.close_type === 'FORCE' ? 'bg-red-50 text-red-600 border-red-200 text-[10px]' : 'bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]'}
                                  title={o.force_close_remarks || ''}>
                                  {o.close_type === 'FORCE' ? 'Force-closed' : 'Pure-closed'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtAge(o.age_hours)}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => setStickerJw(o.jw_id)} data-testid={`stickers-${o.jw_id}`}>
                                <Tag className="w-3.5 h-3.5 mr-1" /> USNs
                              </Button>
                            {canClose ? (
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openPure(o)}
                                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`pure-close-btn-${o.jw_id}`}>
                                  <Lock className="w-3.5 h-3.5 mr-1" /> Pure Close
                                </Button>
                                {isAdmin && (
                                  <Button size="sm" variant="outline" onClick={() => { setForceOrder(o); setForceRemarks(''); }}
                                    className="border-red-500 text-red-600 hover:bg-red-50"
                                    data-testid={`force-close-btn-${o.jw_id}`}>
                                    <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Force
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Closed</span>
                            )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </main>

        {/* PURE CLOSE dialog */}
        <Dialog open={!!pureOrder} onOpenChange={(v) => !v && setPureOrder(null)}>
          <DialogContent data-testid="pure-close-dialog">
            <DialogHeader>
              <DialogTitle>Pure Close — {pureOrder?.jw_id}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Scan the ORIGINAL component USNs of any pieces that came back un-assembled.
                Pure Close unlocks only when every sent piece is accounted for (balance 0).
              </p>
              {pureTally && (
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-gray-50 rounded p-2"><p className="text-[10px] text-gray-500">Order</p><p className="font-bold">{pureTally.sent_qty}</p></div>
                  <div className="bg-emerald-50 rounded p-2"><p className="text-[10px] text-emerald-600">Receipted</p><p className="font-bold text-emerald-700">{pureTally.receipted_qty}</p></div>
                  <div className="bg-amber-50 rounded p-2"><p className="text-[10px] text-amber-600">Returned</p><p className="font-bold text-amber-700">{pureTally.returned_component_qty}</p></div>
                  <div className={`rounded p-2 ${pureTally.balance_qty === 0 ? 'bg-emerald-50' : 'bg-red-50'}`}><p className={`text-[10px] ${pureTally.balance_qty === 0 ? 'text-emerald-600' : 'text-red-500'}`}>Balance</p><p className={`font-bold ${pureTally.balance_qty === 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="pure-balance">{pureTally.balance_qty}</p></div>
                </div>
              )}
              <form onSubmit={scanComponent} className="flex items-center gap-2">
                <Scan className="w-4 h-4 text-gray-500" />
                <Input ref={compRef} value={compUsn} onChange={(e) => setCompUsn(e.target.value)}
                  placeholder="Scan original component USN…" disabled={pureBusy || (pureTally?.balance_qty === 0)}
                  data-testid="component-usn-input" />
                <Button type="submit" disabled={pureBusy || (pureTally?.balance_qty === 0)} data-testid="component-usn-submit">
                  {pureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tally'}
                </Button>
              </form>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPureOrder(null)}>Cancel</Button>
              <Button onClick={confirmPureClose}
                disabled={pureBusy || !pureTally || pureTally.balance_qty !== 0}
                className="bg-emerald-700 hover:bg-emerald-800" data-testid="confirm-pure-close-btn">
                <Lock className="w-4 h-4 mr-2" /> Confirm Pure Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* FORCE CLOSE dialog */}
        <Dialog open={!!forceOrder} onOpenChange={(v) => !v && setForceOrder(null)}>
          <DialogContent data-testid="force-close-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-700">Force Close — {forceOrder?.jw_id}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Use only when the outstanding component USNs are not available to scan.
                {forceOrder && <> Balance unaccounted: <span className="font-bold text-red-600">{forceOrder.balance_qty}</span> of {forceOrder.sent_qty}.</>}
              </p>
              <div>
                <Label>Remarks <span className="text-red-600">*</span></Label>
                <Textarea value={forceRemarks} onChange={(e) => setForceRemarks(e.target.value)}
                  placeholder="Why is this being force-closed?" rows={3}
                  data-testid="force-remarks-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForceOrder(null)}>Cancel</Button>
              <Button onClick={confirmForceClose} disabled={forceBusy || !forceRemarks.trim()}
                className="bg-red-600 hover:bg-red-700" data-testid="confirm-force-close-btn">
                <ShieldAlert className="w-4 h-4 mr-2" /> Force Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <UsnStickerDialog
          open={!!stickerJw}
          onOpenChange={(o) => { if (!o) setStickerJw(null); }}
          title={`USN Stickers · ${stickerJw || ''}`}
          screenKey="flexi-jw-usn"
          fetchGroups={async () => {
            const res = await apiClient.get(`/flexi/jw-orders/${stickerJw}/print-usns`);
            return res.data.groups || [];
          }}
        />
      </div>
    </StaffScanGate>
  );
};

export default JwOrderReport;
